import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { DeleteIcon } from 'assets/icons';
import { PrimaryBtn } from 'components/UI/Buttons';
import FeedbackSnackbar from 'components/UI/FeedbackSnackbar';
import TopicTag from 'components/UI/TopicTag';
import type React from 'react';
import { useState } from 'react';
import {
  type BusinessUnitConversationTaggingConfig,
  type EndReasonConfig,
  TOPIC_COLORS,
  type TopicAssignmentMode,
  type TopicColor,
  type TopicConfig,
} from 'services/types/businessUnit';
import {
  NAVY,
  RADIUS_CARD,
  RADIUS_ITEM,
  SHADOW,
  SURFACE_CONTAINER_LOW,
  SURFACE_CONTAINER_LOWEST,
  YELLOW,
} from 'styles/tokens';

interface Props {
  conversationTaggingConfig: BusinessUnitConversationTaggingConfig | null | undefined;
  onSave: (config: BusinessUnitConversationTaggingConfig) => Promise<unknown> | undefined;
}

const DEFAULT_MODE: TopicAssignmentMode = 'sticky';

const MODE_DESCRIPTIONS: Record<TopicAssignmentMode, string> = {
  sticky: 'El agente lo asigna automáticamente. Una vez puesto, no se quita solo. El usuario puede quitarlo.',
  manual: 'Solo el usuario puede asignar o quitar este tag. El agente no lo toca.',
};

const TOPIC_ASSINGMENT_MODES: TopicAssignmentMode[] = ['sticky', 'manual'];

const TOPIC_LONG_ASSIGNMENT_MODE_LABELS: Record<TopicAssignmentMode, string> = {
  sticky: 'Automático (recomendado)',
  manual: 'Solo manual',
};

const TOPIC_SHORT_ASSIGNMENT_MODE_LABELS: Record<TopicAssignmentMode, string> = {
  sticky: 'Automático',
  manual: 'Manual',
};

// ── Shared topic form ──────────────────────────────────────────────────────────

interface TopicFormValues {
  name: string;
  description: string;
  color: TopicColor;
  mode: TopicAssignmentMode;
}

interface TopicFormProps extends TopicFormValues {
  nameField: boolean;
  onChange: (patch: Partial<TopicFormValues>) => void;
  onSubmit: () => void;
  onCancel: () => void;
  submitLabel: string;
  submitDisabled?: boolean;
}

const TopicForm: React.FC<TopicFormProps> = ({
  nameField,
  name,
  description,
  color,
  mode,
  onChange,
  onSubmit,
  onCancel,
  submitLabel,
  submitDisabled,
}) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
      bgcolor: SURFACE_CONTAINER_LOW,
      borderRadius: RADIUS_CARD,
      p: 2.5,
      mb: 1,
    }}
  >
    {/* Row 1: Tag/Name + Color picker */}
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
      {nameField ? (
        <TextField
          label="Nombre del tag"
          size="small"
          value={name}
          onChange={(e) => onChange({ name: e.target.value })}
          onKeyDown={(e) => {
            if (e.key === 'Enter') onSubmit();
            if (e.key === 'Escape') onCancel();
          }}
          sx={{ width: 180 }}
          autoFocus
        />
      ) : (
        <Box sx={{ flexShrink: 0 }}>
          <TopicTag topicName={name} color={color} variant="filled" />
        </Box>
      )}
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
        {TOPIC_COLORS.map((c) => (
          <Box
            key={c}
            onClick={() => onChange({ color: c })}
            sx={{
              width: 22,
              height: 22,
              borderRadius: '50%',
              backgroundColor: c,
              cursor: 'pointer',
              outline: color === c ? `2.5px solid ${c}` : 'none',
              outlineOffset: 2,
              opacity: color === c ? 1 : 0.7,
              transition: 'opacity 0.15s',
              '&:hover': { opacity: 1 },
            }}
          />
        ))}
      </Box>
    </Box>

    {/* Row 2: Mode + Description */}
    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
      <FormControl size="small" sx={{ width: 200, flexShrink: 0 }}>
        <InputLabel>Modo</InputLabel>
        <Select
          label="Modo"
          value={mode}
          onChange={(e) => onChange({ mode: e.target.value as TopicAssignmentMode })}
          renderValue={(v) => (
            <Typography variant="body2">{TOPIC_LONG_ASSIGNMENT_MODE_LABELS[v as TopicAssignmentMode]}</Typography>
          )}
        >
          {TOPIC_ASSINGMENT_MODES.map((m) => (
            <MenuItem key={m} value={m}>
              <Box>
                <Typography variant="body2">{TOPIC_LONG_ASSIGNMENT_MODE_LABELS[m]}</Typography>
                <Typography variant="caption" sx={{ color: NAVY, opacity: 0.5 }}>
                  {MODE_DESCRIPTIONS[m]}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <TextField
        label="Descripcion (opcional)"
        size="small"
        value={description}
        onChange={(e) => onChange({ description: e.target.value })}
        placeholder="Ej: Cuando el cliente pregunta por precio"
        sx={{ flex: 1, minWidth: 180 }}
        autoFocus={!nameField}
        onKeyDown={(e) => {
          if (e.key === 'Enter') onSubmit();
          if (e.key === 'Escape') onCancel();
        }}
      />
    </Box>

    {/* Row 3: Buttons */}
    <Box sx={{ display: 'flex', gap: 1 }}>
      <PrimaryBtn
        size="small"
        variant="contained"
        onClick={onSubmit}
        disabled={submitDisabled}
        sx={{
          bgcolor: YELLOW,
          color: NAVY,
          '&:hover': { bgcolor: YELLOW, opacity: 0.85 },
          '&.Mui-disabled': { bgcolor: `rgba(21, 24, 58, 0.08)`, color: `rgba(21, 24, 58, 0.3)` },
        }}
      >
        {submitLabel}
      </PrimaryBtn>
      <PrimaryBtn
        size="small"
        variant="outlined"
        onClick={onCancel}
        sx={{ color: NAVY, borderColor: `rgba(21, 24, 58, 0.15)`, '&:hover': { borderColor: NAVY } }}
      >
        Cancelar
      </PrimaryBtn>
    </Box>
  </Box>
);

// ── Shared reason form ────────────────────────────────────────────────────────

interface ReasonFormValues {
  name: string;
  description: string;
}

interface ReasonFormProps extends ReasonFormValues {
  nameField: boolean;
  onChange: (patch: Partial<ReasonFormValues>) => void;
  onSubmit: () => void;
  onCancel: () => void;
  submitLabel: string;
  submitDisabled?: boolean;
}

const ReasonForm: React.FC<ReasonFormProps> = ({
  nameField,
  name,
  description,
  onChange,
  onSubmit,
  onCancel,
  submitLabel,
  submitDisabled,
}) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
      bgcolor: SURFACE_CONTAINER_LOW,
      borderRadius: RADIUS_CARD,
      p: 2.5,
      mb: 1,
    }}
  >
    {nameField ? (
      <TextField
        label="Nombre del motivo"
        size="small"
        fullWidth
        value={name}
        onChange={(e) => onChange({ name: e.target.value })}
        onKeyDown={(e) => {
          if (e.key === 'Enter') onSubmit();
          if (e.key === 'Escape') onCancel();
        }}
        autoFocus
      />
    ) : (
      <Typography variant="body2" sx={{ fontWeight: 500, color: NAVY, fontFamily: 'Jost, sans-serif' }}>
        {name}
      </Typography>
    )}

    <TextField
      label="Descripcion (opcional)"
      size="small"
      fullWidth
      value={description}
      onChange={(e) => onChange({ description: e.target.value })}
      placeholder="Ej: Cuando el cliente deja de responder"
      autoFocus={!nameField}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onSubmit();
        if (e.key === 'Escape') onCancel();
      }}
    />

    <Box sx={{ display: 'flex', gap: 1 }}>
      <PrimaryBtn
        size="small"
        variant="contained"
        onClick={onSubmit}
        disabled={submitDisabled}
        sx={{
          bgcolor: YELLOW,
          color: NAVY,
          '&:hover': { bgcolor: YELLOW, opacity: 0.85 },
          '&.Mui-disabled': { bgcolor: `rgba(21, 24, 58, 0.08)`, color: `rgba(21, 24, 58, 0.3)` },
        }}
      >
        {submitLabel}
      </PrimaryBtn>
      <PrimaryBtn
        size="small"
        variant="outlined"
        onClick={onCancel}
        sx={{ color: NAVY, borderColor: `rgba(21, 24, 58, 0.15)`, '&:hover': { borderColor: NAVY } }}
      >
        Cancelar
      </PrimaryBtn>
    </Box>
  </Box>
);

const switchNavySx = {
  '& .MuiSwitch-switchBase.Mui-checked': { color: NAVY },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: NAVY },
} as const;

// ── Main section ──────────────────────────────────────────────────────────────

const EMPTY_TOPIC_FORM: TopicFormValues = {
  name: '',
  description: '',
  color: TOPIC_COLORS[4],
  mode: DEFAULT_MODE,
};

const TopicsConfigSection: React.FC<Props> = ({ conversationTaggingConfig, onSave }) => {
  const topics = conversationTaggingConfig?.topics ?? [];
  const endReasons = conversationTaggingConfig?.endReasons ?? [];
  const allowFreeFormTopics = conversationTaggingConfig?.allowFreeFormTopics ?? false;
  const allowFreeFormEndReasons = conversationTaggingConfig?.allowFreeFormEndReasons ?? false;

  // New topic form
  const [showTopicForm, setShowTopicForm] = useState(false);
  const [newTopic, setNewTopic] = useState<TopicFormValues>(EMPTY_TOPIC_FORM);

  // Edit topic form
  const [editingTopicIndex, setEditingTopicIndex] = useState<number | null>(null);
  const [editTopic, setEditTopic] = useState<TopicFormValues>(EMPTY_TOPIC_FORM);

  // New end reason form
  const [showReasonForm, setShowReasonForm] = useState(false);
  const [newReason, setNewReason] = useState<ReasonFormValues>({ name: '', description: '' });

  // Edit end reason form
  const [editingReasonIndex, setEditingReasonIndex] = useState<number | null>(null);
  const [editReason, setEditReason] = useState<ReasonFormValues>({ name: '', description: '' });

  const [savedSnackbar, setSavedSnackbar] = useState(false);
  const [errorSnackbar, setErrorSnackbar] = useState(false);

  const save = (
    updatedTopics: TopicConfig[],
    updatedEndReasons: EndReasonConfig[],
    updatedAllowFreeFormTopics: boolean,
    updatedAllowFreeFormEndReasons: boolean
  ) => {
    const result = onSave({
      topics: updatedTopics,
      endReasons: updatedEndReasons,
      allowFreeFormTopics: updatedAllowFreeFormTopics,
      allowFreeFormEndReasons: updatedAllowFreeFormEndReasons,
    });
    if (result instanceof Promise) {
      result.then(() => setSavedSnackbar(true)).catch(() => setErrorSnackbar(true));
    }
  };

  const handleAddTopic = () => {
    if (!newTopic.name.trim()) return;
    const topic: TopicConfig = {
      name: newTopic.name.trim(),
      description: newTopic.description.trim() || null,
      color: newTopic.color,
      isActive: true,
      assignmentMode: newTopic.mode,
    };
    save([...topics, topic], endReasons, allowFreeFormTopics, allowFreeFormEndReasons);
    setNewTopic(EMPTY_TOPIC_FORM);
    setShowTopicForm(false);
  };

  const handleStartEditTopic = (index: number) => {
    const t = topics[index];
    if (!t) return;
    setEditingTopicIndex(index);
    setEditTopic({
      name: t.name,
      description: t.description ?? '',
      color: t.color,
      mode: t.assignmentMode,
    });
  };

  const handleSaveEditTopic = () => {
    if (editingTopicIndex === null) return;
    const updated = topics.map((t, i) =>
      i === editingTopicIndex
        ? {
            ...t,
            description: editTopic.description.trim() || null,
            color: editTopic.color,
            assignmentMode: editTopic.mode,
          }
        : t
    );
    save(updated, endReasons, allowFreeFormTopics, allowFreeFormEndReasons);
    setEditingTopicIndex(null);
  };

  const handleToggleTopic = (index: number) => {
    const updated = topics.map((t, i) => (i === index ? { ...t, isActive: !t.isActive } : t));
    save(updated, endReasons, allowFreeFormTopics, allowFreeFormEndReasons);
  };

  const handleDeleteTopic = (index: number) => {
    save(
      topics.filter((_, i) => i !== index),
      endReasons,
      allowFreeFormTopics,
      allowFreeFormEndReasons
    );
  };

  const handleDeleteReason = (index: number) => {
    save(
      topics,
      endReasons.filter((_, i) => i !== index),
      allowFreeFormTopics,
      allowFreeFormEndReasons
    );
  };

  const handleAddReason = () => {
    if (!newReason.name.trim()) return;
    const reason: EndReasonConfig = {
      name: newReason.name.trim(),
      description: newReason.description.trim() || null,
    };
    save(topics, [...endReasons, reason], allowFreeFormTopics, allowFreeFormEndReasons);
    setNewReason({ name: '', description: '' });
    setShowReasonForm(false);
  };

  const handleStartEditReason = (index: number) => {
    const r = endReasons[index];
    if (!r) return;
    setEditingReasonIndex(index);
    setEditReason({ name: r.name, description: r.description ?? '' });
  };

  const handleSaveEditReason = () => {
    if (editingReasonIndex === null) return;
    const updated = endReasons.map((r, i) =>
      i === editingReasonIndex
        ? { ...r, name: editReason.name.trim() || r.name, description: editReason.description.trim() || null }
        : r
    );
    save(topics, updated, allowFreeFormTopics, allowFreeFormEndReasons);
    setEditingReasonIndex(null);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          display: 'flex',
          gap: 3,
          flexDirection: { xs: 'column', lg: 'row' },
          alignItems: 'flex-start',
        }}
      >
        {/* ── Tags card ── */}
        <Box
          sx={{
            flex: 1,
            width: { xs: '100%', lg: 'auto' },
            minHeight: { xs: 300, lg: 0 },
            display: 'flex',
            flexDirection: 'column',
            bgcolor: SURFACE_CONTAINER_LOWEST,
            borderRadius: RADIUS_CARD,
            boxShadow: SHADOW,
            p: 3,
            minWidth: 0,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, color: NAVY, fontFamily: 'Jost, sans-serif', mb: 0.5 }}>
            Tags
          </Typography>
          <Typography variant="body2" sx={{ mb: 2.5, color: NAVY, opacity: 0.5, fontFamily: 'Jost, sans-serif' }}>
            Define los tags que el agente puede asignar a los clientes y cómo se gestionan.
          </Typography>

          {topics.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
              {topics.map((topic, index) => (
                <Box key={topic.name}>
                  {editingTopicIndex === index ? (
                    <TopicForm
                      nameField={false}
                      {...editTopic}
                      onChange={(patch) => setEditTopic((prev) => ({ ...prev, ...patch }))}
                      onSubmit={handleSaveEditTopic}
                      onCancel={() => setEditingTopicIndex(null)}
                      submitLabel="Guardar"
                    />
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        py: 1,
                        px: 1.5,
                        borderRadius: RADIUS_ITEM,
                        transition: 'background-color 0.15s ease',
                        '&:hover': { bgcolor: SURFACE_CONTAINER_LOW },
                      }}
                    >
                      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'row', gap: 2, minWidth: 0 }}>
                        <TopicTag
                          topicName={topic.name}
                          color={topic.color}
                          inactive={!topic.isActive}
                          variant="filled"
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            color: NAVY,
                            opacity: 0.35,
                            fontSize: '0.68rem',
                            display: { xs: 'none', sm: 'inline' },
                          }}
                        >
                          {TOPIC_SHORT_ASSIGNMENT_MODE_LABELS[topic.assignmentMode]}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: NAVY, opacity: 0.5, display: { xs: 'none', sm: 'inline' } }}
                        >
                          {topic.description}
                        </Typography>
                      </Box>
                      <Tooltip title={topic.isActive ? 'Desactivar' : 'Activar'}>
                        <Switch
                          size="small"
                          checked={topic.isActive}
                          onChange={() => handleToggleTopic(index)}
                          sx={switchNavySx}
                        />
                      </Tooltip>
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => handleStartEditTopic(index)}>
                          <EditIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Eliminar tag">
                        <IconButton size="small" color="error" onClick={() => handleDeleteTopic(index)}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          )}
          {topics.length === 0 && (
            <Typography variant="body2" sx={{ color: NAVY, opacity: 0.4, mb: 2, fontFamily: 'Jost, sans-serif' }}>
              No hay tags configurados todavía.
            </Typography>
          )}

          {showTopicForm && (
            <TopicForm
              nameField
              {...newTopic}
              onChange={(patch) => setNewTopic((prev) => ({ ...prev, ...patch }))}
              onSubmit={handleAddTopic}
              onCancel={() => {
                setShowTopicForm(false);
                setNewTopic(EMPTY_TOPIC_FORM);
              }}
              submitLabel="Agregar"
              submitDisabled={!newTopic.name.trim()}
            />
          )}

          {/* Action bar */}
          <Box
            sx={{
              bgcolor: SURFACE_CONTAINER_LOW,
              borderRadius: RADIUS_CARD,
              px: 2,
              py: 1.5,
              mt: 'auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.5,
              flexWrap: 'wrap',
            }}
          >
            {!showTopicForm && (
              <PrimaryBtn
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setShowTopicForm(true)}
                sx={{
                  bgcolor: NAVY,
                  color: '#fff',
                  fontSize: '0.75rem',
                  minWidth: 'auto',
                  height: 32,
                  px: 1.5,
                  '&:hover': { bgcolor: NAVY, opacity: 0.9 },
                }}
              >
                Agregar tag
              </PrimaryBtn>
            )}
            <FormControlLabel
              sx={{ m: 0, ml: 'auto' }}
              control={
                <Switch
                  checked={allowFreeFormTopics}
                  onChange={(e) => save(topics, endReasons, e.target.checked, allowFreeFormEndReasons)}
                  sx={switchNavySx}
                />
              }
              label={
                <Typography variant="caption" sx={{ color: NAVY, opacity: 0.5, fontFamily: 'Jost, sans-serif' }}>
                  Permitir tags libres
                </Typography>
              }
            />
          </Box>
        </Box>

        {/* ── Motivos de término card ── */}
        <Box
          sx={{
            flex: 1,
            width: { xs: '100%', lg: 'auto' },
            minHeight: { xs: 300, lg: 0 },
            display: 'flex',
            flexDirection: 'column',
            bgcolor: SURFACE_CONTAINER_LOWEST,
            borderRadius: RADIUS_CARD,
            boxShadow: SHADOW,
            p: 3,
            minWidth: 0,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, color: NAVY, fontFamily: 'Jost, sans-serif', mb: 0.5 }}>
            Motivos de término
          </Typography>
          <Typography variant="body2" sx={{ mb: 2.5, color: NAVY, opacity: 0.5, fontFamily: 'Jost, sans-serif' }}>
            Opciones que el agente puede usar al clasificar por qué terminó una conversación.
          </Typography>

          {endReasons.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
              {endReasons.map((reason, index) => (
                <Box key={reason.name}>
                  {editingReasonIndex === index ? (
                    <ReasonForm
                      nameField={false}
                      {...editReason}
                      onChange={(patch) => setEditReason((prev) => ({ ...prev, ...patch }))}
                      onSubmit={handleSaveEditReason}
                      onCancel={() => setEditingReasonIndex(null)}
                      submitLabel="Guardar"
                    />
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        py: 1,
                        px: 1.5,
                        borderRadius: RADIUS_ITEM,
                        transition: 'background-color 0.15s ease',
                        '&:hover': { bgcolor: SURFACE_CONTAINER_LOW },
                      }}
                    >
                      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'row', gap: 2, minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 500, color: NAVY, fontFamily: 'Jost, sans-serif' }}
                        >
                          {reason.name}
                        </Typography>
                        {reason.description && (
                          <Typography variant="caption" sx={{ color: NAVY, opacity: 0.5 }}>
                            {reason.description}
                          </Typography>
                        )}
                      </Box>
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => handleStartEditReason(index)}>
                          <EditIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Eliminar motivo">
                        <IconButton size="small" color="error" onClick={() => handleDeleteReason(index)}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          )}
          {endReasons.length === 0 && (
            <Typography variant="body2" sx={{ color: NAVY, opacity: 0.4, mb: 2, fontFamily: 'Jost, sans-serif' }}>
              No hay motivos configurados. El agente usará texto libre.
            </Typography>
          )}

          {showReasonForm && (
            <ReasonForm
              nameField
              {...newReason}
              onChange={(patch) => setNewReason((prev) => ({ ...prev, ...patch }))}
              onSubmit={handleAddReason}
              onCancel={() => {
                setShowReasonForm(false);
                setNewReason({ name: '', description: '' });
              }}
              submitLabel="Agregar"
              submitDisabled={!newReason.name.trim()}
            />
          )}

          {/* Action bar */}
          <Box
            sx={{
              bgcolor: SURFACE_CONTAINER_LOW,
              borderRadius: RADIUS_CARD,
              px: 2,
              py: 1.5,
              mt: 'auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.5,
              flexWrap: 'wrap',
            }}
          >
            {!showReasonForm && (
              <PrimaryBtn
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setShowReasonForm(true)}
                sx={{
                  bgcolor: NAVY,
                  color: '#fff',
                  fontSize: '0.75rem',
                  minWidth: 'auto',
                  height: 32,
                  px: 1.5,
                  '&:hover': { bgcolor: NAVY, opacity: 0.9 },
                }}
              >
                Agregar motivo
              </PrimaryBtn>
            )}
            <FormControlLabel
              sx={{ m: 0, ml: 'auto' }}
              control={
                <Switch
                  checked={allowFreeFormEndReasons}
                  onChange={(e) => save(topics, endReasons, allowFreeFormTopics, e.target.checked)}
                  sx={switchNavySx}
                />
              }
              label={
                <Typography variant="caption" sx={{ color: NAVY, opacity: 0.5, fontFamily: 'Jost, sans-serif' }}>
                  Permitir motivos libres
                </Typography>
              }
            />
          </Box>
        </Box>
      </Box>

      <FeedbackSnackbar
        open={savedSnackbar}
        onClose={() => setSavedSnackbar(false)}
        message="Cambios guardados"
        severity="success"
      />
      <FeedbackSnackbar
        open={errorSnackbar}
        onClose={() => setErrorSnackbar(false)}
        message="Error al guardar los cambios"
        severity="error"
      />
    </Box>
  );
};

export default TopicsConfigSection;
